import type { TabsProps } from 'antd';
import type { TruckClass } from '../../../../reference-data/reference-data.api';
import { TruckServicePlanTab } from '../../../../maintenance/features/truck-service/public/TruckServicePlanTab';
import { TruckServiceRecordsTab } from '../../../../maintenance/features/truck-service/public/TruckServiceRecordsTab';
import { TruckDetailsTab } from './TruckDetailsTab';
import { TruckDocuments } from './TruckDocuments';
import { TruckHistoryTab } from './TruckHistoryTab';
import { TruckOwnershipTab } from './TruckOwnershipTab';
import { TRUCK_TAB_LABELS } from '../trucks.paths';
import type { TruckDetail } from '../trucks.api';
import type { useTruckDetail } from '../hooks/use-truck-detail';

interface BuildTruckDetailTabsArgs {
  truck: TruckDetail;
  truckClasses: TruckClass[];
  canManageTruck: boolean;
  canManageDocuments: boolean;
  canViewMaintenance: boolean;
  detail: ReturnType<typeof useTruckDetail>;
}

/** The tabs of a truck's own page — its own function so `TruckDetailPage` stays a thin state-to-props wire-up. */
export function buildTruckDetailTabs({
  truck,
  truckClasses,
  canManageTruck,
  canManageDocuments,
  canViewMaintenance,
  detail,
}: BuildTruckDetailTabsArgs): TabsProps['items'] {
  return [
    {
      key: 'details',
      label: TRUCK_TAB_LABELS.details,
      children: (
        <TruckDetailsTab
          truck={truck}
          truckClasses={truckClasses}
          canManage={canManageTruck}
          error={detail.updateError}
          saving={detail.saving}
          onSave={detail.update}
          onRecordReading={detail.recordReading}
        />
      ),
    },
    {
      key: 'documents',
      label: TRUCK_TAB_LABELS.documents,
      children: (
        <TruckDocuments
          documents={truck.documents}
          canManage={canManageDocuments}
          submitting={detail.documentSubmitting}
          onAdd={detail.addDocument}
          onOpen={detail.openDocument}
        />
      ),
    },
    {
      key: 'ownership',
      label: TRUCK_TAB_LABELS.ownership,
      children: <TruckOwnershipTab truckId={truck.id} truckRegistration={truck.registration} canManage={canManageTruck} />,
    },
    ...(canViewMaintenance
      ? [
          { key: 'service-plan', label: TRUCK_TAB_LABELS['service-plan'], children: <TruckServicePlanTab truckId={truck.id} classCode={truck.classCode} /> },
          { key: 'service-records', label: TRUCK_TAB_LABELS['service-records'], children: <TruckServiceRecordsTab truckId={truck.id} /> },
        ]
      : []),
    { key: 'history', label: TRUCK_TAB_LABELS.history, children: <TruckHistoryTab truckId={truck.id} statusHistory={truck.statusHistory} /> },
  ];
}
